import React, { FC, useEffect, useState } from 'react';
import { BillingDetails } from '../../shared/orders/payment';
import { PaymentGatewayConfig } from '../APIClient';
import { useTranslation } from '../translations';
import { Fetcher, useConvention, useFetcher, useGlobalState, useUser } from '../utils';
import { makeLazyComponent } from '../utils/lazy';
import { Loading } from './Loading';
import { FormAlert } from './formElements/models/FormAlert';
import { PaymentProps, PaymentState, PaymentUpdate, RichMarkdownIt } from '.';

interface PaymentGateway {
  name: string;
  type: 'stripe' | 'stripeConnect';
  lazyComponent: FC<PaymentProps>;
}

export const paymentGateways: PaymentGateway[] = [
  {
    lazyComponent: makeLazyComponent(
      async () =>
        (await import(/* webpackChunkName: "stripepayment" */ './payment/StripePayment'))
          .StripePayment,
    ),
    name: 'Stripe',
    type: 'stripeConnect',
  },
  {
    lazyComponent: makeLazyComponent(
      async () =>
        (await import(/* webpackChunkName: "stripepayment" */ './payment/StripePayment'))
          .StripePayment,
    ),
    name: 'Stripe',
    type: 'stripe',
  },
];

interface WrapperProps {
  readonly total: number;
  readonly billingDetails: BillingDetails;
  readonly onSuccessTimeout?: number;
  readonly onSuccess: () => void;
}

interface Props extends WrapperProps {
  readonly gatewayConfig: PaymentGatewayConfig;
}

interface ErrorProps {
  readonly error?: string;
}

const PaymentGatewayError: FC<ErrorProps> = ({ error }) => {
  const convention = useConvention();
  const { ts } = useTranslation();
  return (
    <div className="payment-gateway payment-gateway-error">
      <p>{ts('payment_gateway.sorry_we_cant_accept_payments')}</p>
      <p>{ts('payment_gateway.contact_registration_for_assistance', convention.domain)}</p>
      {error && (
        <p className="text-muted">
          <RichMarkdownIt source={ts('payment_gateway.error', error)} />
        </p>
      )}
    </div>
  );
};

export const PaymentGatewayWrapper: FC<WrapperProps> = (props) => {
  const fetcher = useFetcher(async () => await api.getPaymentGateways());
  if (fetcher.error) {
    return <PaymentGatewayError error={fetcher.error.message} />;
  }

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  return <PaymentGatewayComponent {...props} gatewayConfig={fetcher.data} />;
};

const PaymentGatewayComponent: FC<Props> = ({
  gatewayConfig,
  total,
  billingDetails,
  onSuccess,
  onSuccessTimeout = 0,
}) => {
  const { ts } = useTranslation();
  const [paymentState, setPaymentState] = useState<PaymentUpdate>({ state: PaymentState.Pending });
  const user = useUser();
  const convention = useConvention();
  const contacts = useGlobalState((s) => s.config?.contact);

  useEffect(() => {
    if (paymentState.state === PaymentState.Success) {
      setTimeout(onSuccess, onSuccessTimeout);
    }
  }, [paymentState, onSuccess, onSuccessTimeout]);

  // The kiosk mode timeout may log out the user.
  if (!user) {
    return <PaymentGatewayError error={ts('common.not_logged_in')} />;
  }

  if (paymentState.state === PaymentState.Success) {
    return (
      <div className="payment-gateway">
        <FormAlert markdown={ts(paymentState.message!)} type="success" />
      </div>
    );
  }

  for (const gateway of paymentGateways) {
    if (gateway.type === gatewayConfig.type) {
      return (
        <div className="payment-gateway">
          <h4>{ts('common.pay_by_credit_debit', gateway.name)}</h4>
          <p>
            <small>{ts('payment_gateway.processing_by_gatewayname', gateway.name)}</small>
          </p>
          {paymentState.state === PaymentState.Processing && (
            <Loading>
              <p>{ts('common.processing_payment')}</p>
            </Loading>
          )}
          <hr />
          <div className="payment-gateway-form">
            {paymentState.state === PaymentState.Failed && (
              <FormAlert
                markdown={
                  (ts(
                    paymentState.message ?? 'payment_gateway.an_unexpected_error_occurred_please',
                  ),
                  contacts?.email.registration ?? ts('payment_gateway.registration_team'))
                }
                type="error"
              />
            )}
            <gateway.lazyComponent
              billingDetails={billingDetails}
              conventionName={convention.longName}
              key={total}
              onUpdate={setPaymentState}
              paymentConfig={gatewayConfig}
              total={total}
              user={user}
            />
          </div>
        </div>
      );
    }
  }

  return <PaymentGatewayError error={ts('payment_gateway.no_payment_gateways_have_been')} />;
};
